import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #renderingNext = false;
  #renderingPrevious = false;
  #filterGeneration = 0;
  #filtering = false;
  #stockFillTimer;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (this.getAttribute('infinite-scroll') === 'false') return;

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.refreshStockView();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    clearTimeout(this.#stockFillTimer);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.getAttribute('infinite-scroll') === 'false') return;
    this.#filtering = false;
    this.refreshStockView();
    this.#observeViewMore();
    this.dispatchEvent(new CustomEvent('bb:results-updated'));
  }

  async loadAllPages(signal) {
    const generation = this.#filterGeneration;
    const started = Date.now();
    this.setAttribute('data-print-length-loading', '');
    try {
      while (this.isConnected && !signal?.aborted && generation === this.#filterGeneration) {
        if (Date.now() - started > 45000) throw new Error('Collection loading timed out');
        const previous = this.#getPage('previous');
        const next = this.#getPage('next');
        const wasBusy = this.#renderingPrevious || this.#renderingNext || this.#filtering;
        const countBefore = this.querySelectorAll('[ref="grid"] > [ref="cards[]"]').length;
        if (this.#shouldUsePage(previous)) await this.#renderPreviousPage();
        else if (this.#shouldUsePage(next)) await this.#renderNextPage();
        else return true;
        if (!wasBusy && generation === this.#filterGeneration && !signal?.aborted && this.querySelectorAll('[ref="grid"] > [ref="cards[]"]').length === countBefore) throw new Error('Collection page could not be loaded');
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      return false;
    } finally { this.removeAttribute('data-print-length-loading'); }
  }

  /** Keep sold-out whites after every available product, including later pages. */
  refreshStockView() {
    if (this.#filtering) return;
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const stockFirst = this.dataset.stockOrder === 'available-first';
    const stockOnly = this.hasAttribute('data-stock-only');
    const pages = new Set(cards.map((card) => Number(card.dataset.page)));
    const pending = stockFirst && pages.size < Number(grid.dataset.lastPage);

    for (const card of cards) {
      const soldOut = card.dataset.productAvailable === 'false';
      card.classList.toggle('bb-stock-pending-product', soldOut && pending);
      card.classList.toggle('bb-stock-filtered-product', soldOut && stockOnly);
    }

    if (stockFirst) {
      // Stable partition: retain the selected sort order within each stock group.
      let firstSoldOut = null;
      for (const card of cards) {
        if (card.dataset.productAvailable === 'false') {
          firstSoldOut ??= card;
        } else if (firstSoldOut) {
          grid.insertBefore(card, firstSoldOut);
        }
      }
    }

    clearTimeout(this.#stockFillTimer);
    if (stockFirst || stockOnly) {
      this.#stockFillTimer = setTimeout(() => this.#fillStockViewport(), 0);
    }
  }

  #fillStockViewport() {
    if (!this.isConnected || this.#filtering) return;
    // A direct link to page 2 must also include the preceding available whites.
    if (this.dataset.stockOrder === 'available-first' && this.#shouldUsePage(this.#getPage('previous'))) {
      this.#renderPreviousPage();
      return;
    }
    const sentinel = this.querySelector('[ref="viewMoreNext"]');
    if (!sentinel || !this.#shouldUsePage(this.#getPage('next'))) return;
    // Hidden sold-out cards can leave the sentinel continuously visible, without
    // another IntersectionObserver event. Continue until the viewport is filled.
    if (sentinel.getBoundingClientRect().top <= window.innerHeight + 1200) {
      this.#renderNextPage();
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    const rootMargin = this.getAttribute('infinite-scroll-root-margin') || '100px';

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) {
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.warn('Unable to load collection page', error);
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#filterGeneration;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (generation === this.#filterGeneration) this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNext || this.#filtering) return;
    this.#renderingNext = true;
    const generation = this.#filterGeneration;
    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        await this.#fetchPage('next');
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      if (generation !== this.#filterGeneration || !this.isConnected) return;
      grid.append(...nextPageItemElements);
      this.updatedCallback();

      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }

      if (!this.hasAttribute('data-print-length-loading')) history.pushState('', '', nextPage.url.toString());

      if (this.hasAttribute('eager-pagination')) {
        // Let the component refresh its card refs before resolving the following page.
        setTimeout(() => this.#fetchPage('next'), 0);
      } else {
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      }
    } finally {
      this.#renderingNext = false;
      if (generation !== this.#filterGeneration) this.refreshStockView();
    }
  }

  async #renderPreviousPage() {
    if (this.#renderingPrevious || this.#filtering) return;
    this.#renderingPrevious = true;
    const generation = this.#filterGeneration;
    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        await this.#fetchPage('previous');
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (generation !== this.#filterGeneration || !this.isConnected) return;
      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      this.updatedCallback();

      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }

      if (!this.hasAttribute('data-print-length-loading')) history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement && !this.hasAttribute('data-print-length-loading')) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#renderingPrevious = false;
      if (generation !== this.#filterGeneration) this.refreshStockView();
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = Array.from(this.querySelectorAll('[ref="grid"] > [ref="cards[]"]'));
    const isPrevious = type === 'previous';

    // Stock grouping changes DOM order, so use source page boundaries.
    const sourcePages = cards.map((card) => Number(card.dataset.page));
    if (!sourcePages.length) return;
    const currentCardPage = isPrevious ? Math.min(...sourcePages) : Math.max(...sourcePages);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#filterGeneration += 1;
    this.#filtering = true;
    clearTimeout(this.#stockFillTimer);
    this.pages.clear();

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage || mutations.some((mutation) => mutation.type === 'childList')) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        this.updatedCallback();

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
